import { isEmpty, isNil } from 'lodash';
import { message } from 'antd';
import { ON_THE_WHOLE_STRATUM, SEPARATE_STRATUM, WITHOUT_STRATUM } from './additional-stratums';
import { getDataFromLocalStorage, putDataIntoLocalStorage } from '@/lib/localStorage';
import { NODE_TYPE } from '@/lib/nodeType';
import tableWidgetModel from '../mining-and-pumping/table-widget';

const INDICATORS_TEMPLATE_BY_FIELD_PDB_KEY = 'production-injection/by-field/chart/indicators-template';
const INDICATORS_TEMPLATE_BY_WELL_PDB_KEY = 'production-injection/by-well/chart/indicators-template';

const DEFAULT_CHART_CHARACTERISTICS_KEYS_BY_WELL = [
    'FactorExploit',
    'DebitOilPerDayTonne',
    'DebitOilPerDayValue',
    'DebitFluidPerDayTonne',
    'DebitFluidPerDayValue',
    'WaterCutPercentByValue',
    'WaterCutPercentByTonne',
    'DebitGasDisPerDayValueTh',
    'DebitGasCapPerDayValueTh',
    'DebitCondPerDayTonne',
    'DebitGasFreePerDayValueTh',
    'InjectivityWaterPerDayValue',
    'InjectivityGasPerDayValue',
    'DepthSetPumpMeters',
    'LevelDynamic',
    'LevelResDynamicMeters',
    'Method',
    'OpFactorCalc',
    'OpFactorDB',
    'GDISPressure',
    'FactorInjectivity',
];

const DEFAULT_CHART_CHARACTERISTICS_KEYS_BY_FIELD = [
    'FactorExploit',
    'DebitOilPerDayTonne',
    'DebitFluidPerDayTonne',
    'DebitCondPerDayTonne',
    'DebitOilPerDayValue',
    'DebitFluidPerDayValue',
    'DebitGasDisPerDayValueTh',
    'DebitGasCapPerDayValueTh',
    'DebitGasFreePerDayValueTh',
    'InjectivityWaterPerDayValue',
    'InjectivityGasPerDayValue',
    'WaterCutPercentByValue',
    'WaterCutPercentByTonne',
];

const COMPARE_MODE_CHARACTERISTICS_KEYS = [
    'DebitOilPerDayTonne',
    'DebitFluidPerDayTonne',
    'WaterCutPercentByValue',
    'InjectGasCumSumValueTh',
    'InjectGasPerDayValue',
    'InjectGasPeriodValue',
    'InjectWaterCumSumValueTh',
    'InjectWaterPerDayValue',
    'InjectWaterPeriodValue',
    'InjectivityGasPerDayValue',
    'InjectivityWaterPerDayValue',
    'PresHead',
];

export const ON_THE_WHOLE_STRATUM_SHOWING_SEPARATE_STRATUM_INDICATORS = [
    'FactorExploit',
    'OpFactorCalc',
    'OpFactorDB',
    'PresBTMHLOWC',
    'PresBTMHLCap',
    'PresSaturation',
    'PresBTMHLCalcOWC',
    'PresBTMHLCalcTOP',
    'PresStratOWC',
    'PresStratCap',
    'FactorInjectivity',
];
// const COMPARE_MODE_AXES_KEYS = [
//     'DebitDayTonne',
//     'WaterCut',
//     'InjectGasCumSumValueTh',
//     'InjectGasPerDayValue',
//     'InjectGasValue',
//     'InjectWaterCumSumValueTh',
//     'InjectWaterPerDayValue',
//     'InjectWaterValue',
// ];

const TEMPLATE_INDICATOR_STATE = {
    marked: false,
    checked: false,
    indeterminate: false,
    visible: true,
    precision: 2,
    [ON_THE_WHOLE_STRATUM.id]: false,
    [SEPARATE_STRATUM.id]: false,
};

const creatingTemplate = (indicators, isField) => {
    if (indicators) {
        const updatedIndicators = { ...indicators };
        const defaultIndicators = isField
            ? DEFAULT_CHART_CHARACTERISTICS_KEYS_BY_FIELD
            : DEFAULT_CHART_CHARACTERISTICS_KEYS_BY_WELL;
        defaultIndicators.forEach(key => {
            if (updatedIndicators[key]) {
                updatedIndicators[key].checked = true;
                updatedIndicators[key][SEPARATE_STRATUM.id] = true;
                updatedIndicators[key].indeterminate = false;
                updatedIndicators[key][ON_THE_WHOLE_STRATUM.id] = true;
            }
        });
        return updatedIndicators;
    }

    return {};
};

const chartsWidgetModel = {
    state: {
        activeStratums: null,
        activeAxes: null,
        currentModal: 'none',

        isStratumsTimelineVisible: true,
        legendVisible: false,
        indicators: null,
        indicatorsTemplate: null,

        dataChart: null,
        stratums: null,
        compareModeChartOptions: null,
        loading: {
            dataChart: false,
            compareModeData: false,
        },
        error: {
            dataChart: false,
        },
        errorMessage: '',
    },
    reducers: {
        setActiveStratums(state, payload) {
            return { ...state, activeStratums: payload };
        },
        setCurrentModal(state, payload) {
            return { ...state, currentModal: payload };
        },
        setErrorMessage(state, payload) {
            return { ...state, errorMessage: payload };
        },
        setIsStratumsTimelineVisible(state, payload) {
            return { ...state, isStratumsTimelineVisible: payload };
        },
        setLegendVisible(state, payload) {
            return { ...state, legendVisible: payload };
        },
        setIndicators(state, payload) {
            return { ...state, indicators: payload };
        },
        setIndicatorsTemplate(state, payload) {
            return { ...state, indicatorsTemplate: payload };
        },
        setActiveAxes(state, payload) {
            return { ...state, activeAxes: payload };
        },
        setDataChart(state, payload) {
            return { ...state, dataChart: payload };
        },
        setStratums(state, payload) {
            if (!payload) return { ...state, stratums: payload };
            return { ...state, stratums: payload };
        },
        setCompareModeChartOptions(state, payload) {
            return { ...state, compareModeChartOptions: payload };
        },
        setLoadingDataChart(state, payload) {
            return {
                ...state,
                loading: {
                    ...state.loading,
                    dataChart: payload,
                },
            };
        },
        setLoadingCompareModeData(state, payload) {
            return {
                ...state,
                loading: {
                    ...state.loading,
                    compareModeData: payload,
                },
            };
        },
        setErrorDataChart(state, payload) {
            return {
                ...state,
                error: {
                    ...state.error,
                    dataChart: payload,
                },
            };
        },
    },

    effects: dispatch => ({
        setIndicatorsTemplate(payload, rootState) {
            if (payload) {
                const byField = rootState.tree.calculationObject?.node === NODE_TYPE.FIELD;
                const pdbKey = byField ? INDICATORS_TEMPLATE_BY_FIELD_PDB_KEY : INDICATORS_TEMPLATE_BY_WELL_PDB_KEY;
                putDataIntoLocalStorage({ key: pdbKey, data: payload });
            }
        },

        async defineIndicatorsTemplate(props = {}) {
            const { byField = false } = props;
            const pdbKey = byField ? INDICATORS_TEMPLATE_BY_FIELD_PDB_KEY : INDICATORS_TEMPLATE_BY_WELL_PDB_KEY;
            const indicatorsTemplatePdb = getDataFromLocalStorage({ key: pdbKey });
            let indicatorsTemplate = indicatorsTemplatePdb?.data;
            if (!indicatorsTemplate) {
                indicatorsTemplate = await this.generateDefaultIndicatorsTemplate();
            }
            this.setIndicatorsTemplate(await this.generateDefaultIndicatorsTemplate());
        },

        generateDefaultIndicatorsTemplate(props, rootState) {
            const byField = rootState.tree.calculationObject?.node === NODE_TYPE.FIELD;
            const { indicators } = rootState.miningAndPumping;

            const data = { mining: {}, injection: {} };

            if (indicators) {
                const indicatorsForProduction = {};
                const indicatorsForInjection = {};

                Object.entries(indicators).forEach(([key, indicator]) => {
                    const { destination, accessory } = indicator;
                    if ((destination ?? []).some(type => type === 'chart')) {
                        if ((accessory ?? []).some(type => type === 'production')) {
                            indicatorsForProduction[key] = { ...indicator, ...TEMPLATE_INDICATOR_STATE };
                        }
                        if ((accessory ?? []).some(type => type === 'injection'))
                            indicatorsForInjection[key] = { ...indicator, ...TEMPLATE_INDICATOR_STATE };
                    }
                });

                data.mining = creatingTemplate(indicatorsForProduction, byField);
                data.injection = creatingTemplate(indicatorsForInjection, byField);
            }
            return data;
        },

        generateIndicatorsAccordingToTemplate(_, rootState) {
            const { stratums, indicatorsTemplate } = rootState.chartsWidget;
            console.log('generateIndicatorsAccordingToTemplate', [rootState.chartsWidget]);
            if (stratums && indicatorsTemplate) {
                const existOnTheWholeStratum = Object.values(stratums).some(({ id }) => id === ON_THE_WHOLE_STRATUM.id);

                const indicators = Object.keys(indicatorsTemplate).reduce((acc, mode) => {
                    Object.values(indicatorsTemplate[mode]).forEach(indicator => {
                        const { key } = indicator;

                        if (!acc[key]) {
                            const extendedIndicator = { ...indicator };
                            Object.values(stratums).forEach(({ id: stratumId }) => {
                                if (stratumId !== ON_THE_WHOLE_STRATUM.id) {
                                    extendedIndicator[stratumId] = indicator[SEPARATE_STRATUM.id];
                                }
                            });
                            if (!existOnTheWholeStratum && !indicator[SEPARATE_STRATUM.id]) {
                                extendedIndicator.indeterminate = false;
                                extendedIndicator.checked = false;
                            }
                            acc[key] = extendedIndicator;
                        }
                    });
                    return acc;
                }, {});

                return indicators;
            }
            return null;
        },

        async prepareIndicatorsStructure() {
            const indicators = this.generateIndicatorsAccordingToTemplate();
            this.setIndicators(indicators);
        },

        async getCompareModeData(payload, rootState) {
            chartsWidgetModel.reducers.setLoadingCompareModeData(true);
            chartsWidgetModel.reducers.setCompareModeChartOptions(null);
            this.setErrorMessage('');
            // const versionsPriority = rootState.versions.active;
            const { activeWell } = payload;

            const { groupPeriod: step, groupSubtotal: total, groupTotal: sum } = rootState.tableWidget;
            const { compareModeWell } = rootState.miningAndPumping;

            const { dataChart: activeWellData, indicators } = rootState.chartsWidget;
            const { axes } = rootState.miningAndPumping;

            const body = {
                // versionIds: versionsPriority,
                step,
                total,
                sum,
                wellId: compareModeWell.id,
            };
            try {
                const COMPARE_MODE_AXES_KEYS = new Set();
                let errorMessage = '';
                let compareModeData = null;
                const compareModeResult = await dispatch.miningAndPumping.fetchDataset({ body });
                if (compareModeResult?.data) {
                    compareModeData = compareModeResult.data?.chart;
                    const [activeWellDatasetByStratum] = activeWellData.filter(o => o?.stratumId != -100);

                    const compareModeDatasetByStratum = compareModeData.find(
                        ({ stratumId }) => stratumId === activeWellDatasetByStratum?.stratumId,
                    );
                    if (!compareModeDatasetByStratum || !activeWellDatasetByStratum)
                        errorMessage = 'Нет совпадения данных по пластам';

                    if (errorMessage) {
                        this.setErrorMessage(errorMessage);
                        throw errorMessage;
                    }

                    const activeWellDataset = COMPARE_MODE_CHARACTERISTICS_KEYS.reduce((acc, indicatorKey) => {
                        const points = activeWellDatasetByStratum.data?.[indicatorKey];
                        if (points) {
                            const { title, lineStyle, typeDiagram, axis, color } = indicators[indicatorKey];
                            COMPARE_MODE_AXES_KEYS.add(axis);
                            acc.push({
                                key: `${indicatorKey}${activeWell.key}`,
                                title: `(${activeWell.title}) ${title}`,
                                axis,
                                points,
                                lineStyle,
                                typeDiagram,
                                color,
                            });
                        }
                        return acc;
                    }, []);
                    const compareModeDataset = COMPARE_MODE_CHARACTERISTICS_KEYS.reduce((acc, indicatorKey) => {
                        const points = compareModeDatasetByStratum.data?.[indicatorKey];
                        if (points) {
                            const { title, lineStyle, typeDiagram, axis, color } = indicators[indicatorKey];
                            COMPARE_MODE_AXES_KEYS.add(axis);
                            acc.push({
                                key: `${indicatorKey}${compareModeWell.id}`,
                                title: `(${compareModeWell.name}) ${title}`,
                                axis,
                                points,
                                lineStyle,
                                typeDiagram,
                                color,
                            });
                        }
                        return acc;
                    }, []);
                    const compareModeAxes = Array.from(COMPARE_MODE_AXES_KEYS)
                        .map(axesId => axes[axesId])
                        .map(e => {
                            if (e.minBound === 0 && e.maxBound === 0) {
                                return { ...e, minBound: null, maxBound: null };
                            } else {
                                return e;
                            }
                        });
                    const compareModeDatasets = [...activeWellDataset, ...compareModeDataset];

                    this.setCompareModeChartOptions({
                        data: compareModeDatasets,
                        axes: compareModeAxes,
                    });
                }
                if (!compareModeData) {
                    errorMessage = 'Нет данных по скважине для сравнения';
                    this.setErrorMessage(errorMessage);
                    throw errorMessage;
                }
            } catch (error) {
                console.error('Error in getCompareModeData:', error);

                chartsWidgetModel.reducers.setErrorMessage(
                    error.message || 'Не удалось получить данные для графика в режиме сравнения',
                );

                message.error(error.message || 'Не удалось получить данные для графика в режиме сравнения');
            }
            chartsWidgetModel.reducers.setLoadingCompareModeData(false);
            dispatch.tableWidget.setLoadingDataSource(false);
            chartsWidgetModel.reducers.setLoadingDataChart(false);
        },
    }),
    selectors: (slice, createSelector) => ({
        filteredData() {
            return createSelector(
                rootState => rootState.chartsWidget.dataChart,
                rootState => rootState.chartsWidget.activeAxes,
                rootState => rootState.chartsWidget.activeStratums,
                rootState => rootState.chartsWidget.stratums,
                rootState => rootState.chartsWidget.indicators,
                (data, activeAxes, activeStratums, stratums, indicators) => {
                    if (!isEmpty(data) && !isEmpty(indicators) && !isEmpty(activeStratums) && !isEmpty(activeAxes)) {
                        const filteredData = data.reduce((accData, { stratumId, data }) => {
                            const isStratumActive = !!activeStratums[stratumId];
                            const isStratumOnTheWhole = stratumId === ON_THE_WHOLE_STRATUM.id;

                            if (!data || isEmpty(data)) return accData;

                            const dataByStratum = Object.entries(indicators).reduce(
                                (accStratumData, [indicatorKey, indicator]) => {
                                    const points = data[indicatorKey] ?? [];

                                    const isIndicatorVisible =
                                        !isNil(points) &&
                                        (activeAxes[indicator.axis] || isNil(indicator.axis)) &&
                                        indicator.visible &&
                                        (indicator.indeterminate || indicator.checked);

                                    if (!isIndicatorVisible || !Array.isArray(points) || !points.length) {
                                        return accStratumData;
                                    }

                                    const baseProps = {
                                        key: `${stratumId}_${indicatorKey}`,
                                        entityKey: indicatorKey,
                                        stratumTitle: stratums[stratumId]?.title ?? null,
                                        title: indicator.title,
                                        lineStyle: indicator.lineStyle,
                                        typeDiagram: indicator.typeDiagram,
                                        axis: indicator.axis,
                                        color: indicator.color || '#000',
                                        gradientColors: indicator.gradientColors ?? null,
                                        points,
                                        marked: indicator?.marked,
                                        precision: indicator.precision,
                                    };

                                    if (isStratumActive && indicator[stratumId] && !isStratumOnTheWhole) {
                                        accStratumData.push(baseProps);
                                    }

                                    if (
                                        activeStratums[ON_THE_WHOLE_STRATUM.id] &&
                                        !isStratumOnTheWhole &&
                                        ON_THE_WHOLE_STRATUM_SHOWING_SEPARATE_STRATUM_INDICATORS.includes(
                                            indicatorKey,
                                        ) &&
                                        indicator[stratumId]
                                    ) {
                                        accStratumData.push({
                                            ...baseProps,
                                            key: `${stratumId}_${indicatorKey}_onTheWhole`,
                                        });
                                    }

                                    if (
                                        isStratumOnTheWhole &&
                                        activeStratums[ON_THE_WHOLE_STRATUM.id] &&
                                        indicator[stratumId]
                                    ) {
                                        accStratumData.push(baseProps);
                                    }

                                    return accStratumData;
                                },
                                [],
                            );

                            if (!isEmpty(dataByStratum)) {
                                accData.push(...dataByStratum);
                            }

                            return accData;
                        }, []);

                        if (!isEmpty(filteredData)) return filteredData;
                    }
                    return [];
                },
            );
        },

        stratumsList() {
            return slice(({ stratums }) =>
                Object.values(stratums ?? {}).reduce((acc, stratum) => {
                    if (stratum.id === ON_THE_WHOLE_STRATUM.id) acc.unshift(stratum);
                    else acc.push(stratum);
                    return acc;
                }, []),
            );
        },

        activeStratumsKeys() {
            return slice(({ activeStratums }) => Object.keys(activeStratums ?? {}).filter(key => activeStratums[key]));
        },

        activeAxesList(models) {
            return createSelector(
                rootState => rootState.miningAndPumping.axes,
                models.chartsWidget.filteredData,
                (axes, filteredData) => {
                    if (!isEmpty(axes) && !isEmpty(filteredData)) {
                        const uniqActiveAxesKeys = [...new Set(filteredData.map(({ axis }) => axis))];
                        return uniqActiveAxesKeys.reduce((acc, key) => {
                            if (axes[key]) {
                                acc.push(axes[key]);
                            }
                            return acc;
                        }, []);
                    }
                    return [];
                },
            );
        },

        chartWidgetTitle(models) {
            return createSelector(
                rootState => rootState.miningAndPumping.compareMode,
                rootState => rootState.miningAndPumping.compareModeWell,
                models.lists.activeItem,
                (compareMode, compareModeWell, activeItem) => {
                    return !compareMode
                        ? 'График'
                        : `График - сравнение скважин ${compareModeWell?.name ?? '###'} и ${
                              activeItem?.title ?? '###'
                          }`;
                },
            );
        },
    }),
};

export default chartsWidgetModel;
